import type { Layout, LeafNode } from './types';

export const uid = (prefix = ''): string => prefix + Math.random().toString(36).slice(2, 9);

export function defaultLayout(initialTab?: { viewId: string; title: string }): LeafNode {
  const viewId = initialTab?.viewId ?? 'start';
  const title = initialTab?.title ?? 'Start';
  return {
    type: 'leaf',
    id: uid('leaf-'),
    tabs: [
      {
        id: `t-${viewId}`,
        viewId,
        title,
        pinned: true,
        ephemeral: false,
      },
    ],
    activeId: `t-${viewId}`,
  };
}

export function findLeafById(
  node: Layout,
  id: string,
): { node: LeafNode; path: Array<'a' | 'b'> } | null {
  return walk(node, id, []);
}

function walk(
  node: Layout,
  id: string,
  path: Array<'a' | 'b'>,
): { node: LeafNode; path: Array<'a' | 'b'> } | null {
  if (node.type === 'leaf') return node.id === id ? { node, path } : null;
  return walk(node.a, id, [...path, 'a']) || walk(node.b, id, [...path, 'b']);
}

export function allLeaves(node: Layout, acc: LeafNode[] = []): LeafNode[] {
  if (node.type === 'leaf') {
    acc.push(node);
    return acc;
  }
  allLeaves(node.a, acc);
  allLeaves(node.b, acc);
  return acc;
}

export function mapTree(node: Layout, fn: (leaf: LeafNode) => LeafNode): Layout {
  if (node.type === 'leaf') return fn(node);
  return { ...node, a: mapTree(node.a, fn), b: mapTree(node.b, fn) };
}

export function replaceLeaf(node: Layout, leafId: string, replacement: Layout): Layout {
  if (node.type === 'leaf') return node.id === leafId ? replacement : node;
  return {
    ...node,
    a: replaceLeaf(node.a, leafId, replacement),
    b: replaceLeaf(node.b, leafId, replacement),
  };
}

export function collapseEmpty(node: Layout): Layout | null {
  if (node.type === 'leaf') return node.tabs.length ? node : null;
  const a = collapseEmpty(node.a);
  const b = collapseEmpty(node.b);
  if (!a && !b) return null;
  if (!a) return b;
  if (!b) return a;
  return { ...node, a, b };
}

/**
 * Drop every tab whose viewId is not allowed (restoring a persisted layout
 * under custom cell mode), repairing each leaf's activeId and collapsing
 * leaves/splits the prune emptied. Null when nothing survives. Lossy by
 * design — the persisted layout is a cache, not data.
 */
export function pruneTabsByView(
  node: Layout,
  allowedViewIds: ReadonlySet<string>,
): Layout | null {
  const pruned = mapTree(node, (leaf) => {
    const tabs = leaf.tabs.filter((t) => allowedViewIds.has(t.viewId));
    if (tabs.length === leaf.tabs.length) return leaf;
    const activeId = tabs.some((t) => t.id === leaf.activeId)
      ? leaf.activeId
      : (tabs[tabs.length - 1]?.id ?? null);
    return { ...leaf, tabs, activeId };
  });
  return collapseEmpty(pruned);
}
